/**
 * Loads one child's academic summary and hands it up.
 *
 * A hook cannot be called in a loop, so each child gets a component whose only
 * job is to run the query and report the result to the dashboard. It renders
 * nothing itself.
 */
import { useEffect } from 'react';
import { useStudentAcademicSummary } from '~/components/StudentAcademics/useStudentAcademicSummary';
import type { StudentSummary } from './parentInbox';

interface Props {
  schoolId: string;
  schoolYearId: string;
  personId: string;
  name: string;
  onLoaded: (summary: StudentSummary) => void;
}

export default function StudentSummaryLoader({
  schoolId, schoolYearId, personId, name, onLoaded,
}: Props) {
  const { data } = useStudentAcademicSummary(schoolId, schoolYearId, personId);
  const summary = data?.studentAcademicSummary;

  useEffect(() => {
    if (!summary) return;
    onLoaded({
      personId,
      name,
      missingAssignments: (summary.missingAssignments ?? []).map((missing) => ({
        assignmentId: missing.assignmentId,
        name: missing.name,
        className: missing.className,
        dueOn: missing.dueOn ? String(missing.dueOn) : null,
      })),
      classGrades: (summary.classGrades ?? []).map((grade) => ({
        transcriptName: grade.transcriptName,
        result: grade.result
          ? {
            percentage: grade.result.percentage,
            letterGrade: grade.result.letterGrade,
            gradeColor: grade.result.gradeColor,
          }
          : null,
      })),
    });
  }, [summary, personId, name, onLoaded]);

  return null;
}
